import {
  Actions,
  closeCurrentDialog,
  DataStatus,
  showNotification,
  Tooltip,
} from 'platform/components';
import {Heading, HStack, Link, Show, Text, VStack} from 'platform/foundation';

import {useEffect, useState} from 'react';

import {isNilOrEmpty, isNonPositive, isNotNilOrEmpty} from 'ramda-adjunct';

import {
  useGetBackgroundRemovalListQuery,
  useGetSalePresetSettingsV2Query,
  useRemoveVehiclePhotosBackgroundMutation,
} from '@dms/api/sales';
import {
  useGetSaleVehicleFromSaleVehicleIdQuery,
  useRemoveSaleVehiclePhotosBackgroundMutation,
} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {settingsRoutes} from '@dms/routes';

import {suffixTestId, TestIdProps} from 'shared/web';

import {usePermissions} from '../../hooks/usePermissions/usePermissions';
import {handleApiError} from '../../utils/handleApiError';
import {BackgroundSelector} from '../BackgroundSelector/BackgroundSelector';

interface BackgroundRemovalDialogPropsBase extends TestIdProps {
  vehiclePhotoIds: string[];
  onConfirm?: () => void;
}

interface WithVehicleId extends BackgroundRemovalDialogPropsBase {
  vehicleId: string;
  saleVehicleId?: never;
  saleVehicleAlbumId?: never;
}

interface WithSaleVehicleId extends BackgroundRemovalDialogPropsBase {
  saleVehicleId: string;
  saleVehicleAlbumId: string;
  vehicleId?: never;
}

export function BackgroundRemovalDialog(props: WithVehicleId | WithSaleVehicleId) {
  const [canConfigureBackgroundRemoval, canSaleVehicleBackgroundRemoval] = usePermissions({
    permissionKeys: ['configureBackgroundRemoval', 'saleVehicleBackgroundRemoval'],
  });

  const canRemoveBackground = canConfigureBackgroundRemoval && canSaleVehicleBackgroundRemoval;

  const {
    data,
    isLoading: isBackgroundRemovalSettingsListLoading,
    isError,
  } = useGetBackgroundRemovalListQuery();
  const [activeBgSettingsIndex, setActiveBgSettingsIndex] = useState(0);

  const {data: saleVehicle, isLoading: isSaleVehicleQueryLoading} =
    useGetSaleVehicleFromSaleVehicleIdQuery(
      {
        vehicleId: props.saleVehicleId ?? '',
      },
      {skip: isNilOrEmpty(props.saleVehicleId)}
    );

  const {data: salePresetToSelectData, isLoading: isSalePresetQueryLoading} =
    useGetSalePresetSettingsV2Query(
      {salePresetId: saleVehicle?.salePresetId ?? ''},
      {skip: isNilOrEmpty(saleVehicle?.salePresetId)}
    );

  const indexForSalePresetBg = data?.findIndex(
    (backgroundRemoval) => backgroundRemoval.id === salePresetToSelectData?.backgroundRemovalId
  );

  const [removePhotosBackground, {isLoading: isRemoveBackgroundsLoading}] =
    useRemoveVehiclePhotosBackgroundMutation();

  const [removeSaleVehiclePhotosBackground, {isLoading: isSaleVehiclePhotosBackgroundsLoading}] =
    useRemoveSaleVehiclePhotosBackgroundMutation();

  useEffect(
    function selectSalePresetBackground() {
      if (indexForSalePresetBg && indexForSalePresetBg !== -1) {
        setActiveBgSettingsIndex(indexForSalePresetBg);
      }
    },
    [indexForSalePresetBg]
  );

  const isLoading =
    isBackgroundRemovalSettingsListLoading || isSaleVehicleQueryLoading || isSalePresetQueryLoading;

  const handleBackgroundRemoval = () => {
    const promise = props.saleVehicleId
      ? removeSaleVehiclePhotosBackground({
          saleVehicleId: props.saleVehicleId,
          saleVehicleAlbumId: props.saleVehicleAlbumId,
          removeSaleVehiclePhotosBackgroundRequestBody: {
            saleVehiclePhotoIds: props.vehiclePhotoIds,
            ...(isNotNilOrEmpty(data?.[activeBgSettingsIndex]) && {
              backgroundRemovalSettingId: data![activeBgSettingsIndex].id,
            }),
          },
        })
      : removePhotosBackground({
          vehicleId: props.vehicleId!,
          body: {
            vehiclePhotoIds: props.vehiclePhotoIds,
          },
        });

    promise
      .unwrap()
      .then(props.onConfirm)
      .then(closeCurrentDialog)
      .then(() => {
        showNotification.success(i18n.t('entity.photo.backgroundRemoval.scheduledNotification'));
      })
      .catch(handleApiError);
  };

  return (
    <DataStatus isLoading={isLoading || isBackgroundRemovalSettingsListLoading} isError={isError}>
      <HStack spacing={6}>
        <VStack spacing={6}>
          <Heading size={3}>{i18n.t('entity.photo.backgroundRemoval.dialogHeadline')}</Heading>
          <Text size="small" alternative>
            {i18n.t('entity.photo.backgroundRemoval.countInformation', {
              count: props.vehiclePhotoIds.length,
            })}
          </Text>
          <Show when={isNotNilOrEmpty(props.saleVehicleId)}>
            <BackgroundSelector
              activeOptionIndex={activeBgSettingsIndex}
              columns={3}
              options={data}
              onClick={setActiveBgSettingsIndex}
            />
          </Show>
          <Text size="xSmall" color="secondary">
            {i18n.t('entity.photo.backgroundRemoval.description')}
          </Text>
          <Tooltip label={!canRemoveBackground ? i18n.t('general.labels.noPermission') : undefined}>
            <Link
              data-testid={suffixTestId('configure-removal', props)}
              href={settingsRoutes.backgroundRemoval}
              title={i18n.t('entity.photo.backgroundRemoval.changeSettings')}
              leftIcon="action/magic_wand"
              isDisabled={!canRemoveBackground}
              size="small"
            />
          </Tooltip>
          <Actions
            data-testid={suffixTestId('actions', props)}
            align="right"
            actions={[
              {
                title: i18n.t('general.actions.cancel'),
                type: 'button',
                variant: 'secondary',
                onClick: closeCurrentDialog,
              },
              {
                title: i18n.t('general.actions.confirm'),
                type: 'button',
                isDisabled: isNonPositive(props.vehiclePhotoIds.length),
                isLoading: isRemoveBackgroundsLoading || isSaleVehiclePhotosBackgroundsLoading,
                onClick: handleBackgroundRemoval,
              },
            ]}
          />
        </VStack>
      </HStack>
    </DataStatus>
  );
}
